/**
 * Career-site conversion events for GTM (GTM-W8RR296G).
 *
 * Primary Ads conversion: outbound click to the FLARETECH HRMOS listing.
 * Event name must stay distinct from AI診断 LP conversions.
 * Do not put AW- send_to labels here — GTM owns the Ads tag.
 */

export const CAREER_APPLY_HRMOS_EVENT = 'career_apply_hrmos';
export const HRMOS_HOST = 'hrmos.co';
export const HRMOS_PATH_PREFIX = '/pages/flaretech-inc';
export const HRMOS_NATS_HOST = 'public.n-ats.hrmos.co';
export const HRMOS_NATS_PATH_PREFIX = '/flaretech/jobs';

const SKIP_PATH_PREFIXES = ['/admin', '/preview'];

export function isSkippedTrackingPath(pathname = '') {
  const path = String(pathname || '').split('?')[0];
  return SKIP_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function isHrmosApplyUrl(href, base = 'https://recruit.flaretech.co.jp') {
  if (!href || typeof href !== 'string') return false;
  try {
    const url = new URL(href, base);
    if (url.hostname === HRMOS_HOST && url.pathname.startsWith(HRMOS_PATH_PREFIX)) {
      return true;
    }
    return url.hostname === HRMOS_NATS_HOST && url.pathname.startsWith(HRMOS_NATS_PATH_PREFIX);
  } catch {
    return false;
  }
}

export function classifyHrmosIntent(anchor) {
  const className = String(anchor?.className || '').toLowerCase();
  const text = String(anchor?.textContent || anchor?.getAttribute?.('aria-label') || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  if (className.includes('casual') || text.includes('カジュアル')) {
    return 'casual';
  }
  return 'apply';
}

export function buildHrmosClickPayload(anchor, pagePath = '/') {
  const href = anchor?.href || anchor?.getAttribute?.('href') || '';
  const rawText = String(anchor?.textContent || anchor?.getAttribute?.('aria-label') || '')
    .replace(/\s+/g, ' ')
    .trim();

  return {
    event_category: 'career',
    link_url: href,
    link_text: rawText.slice(0, 80),
    link_intent: classifyHrmosIntent(anchor),
    page_path: pagePath
  };
}

export function pushCareerEvent(eventName, data = {}) {
  if (typeof window === 'undefined' || !eventName) return false;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...data
  });
  return true;
}

export function closestAnchor(target) {
  if (!target) return null;
  if (typeof target.closest === 'function') {
    return target.closest('a[href]');
  }
  let node = target;
  while (node) {
    const href = node.href || node.getAttribute?.('href');
    if (href && (node.tagName === 'A' || !node.tagName)) return node;
    node = node.parentElement;
  }
  return null;
}

export function handleHrmosDocumentClick(domEvent, { pagePath } = {}) {
  const path = pagePath ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  if (isSkippedTrackingPath(path)) return false;

  const anchor = closestAnchor(domEvent?.target);
  if (!anchor) return false;

  const href = anchor.href || anchor.getAttribute('href') || '';
  if (!isHrmosApplyUrl(href)) return false;

  return pushCareerEvent(CAREER_APPLY_HRMOS_EVENT, buildHrmosClickPayload(anchor, path));
}

let installedCleanup = null;

export function installHrmosClickTracking() {
  if (typeof document === 'undefined') return () => {};
  if (installedCleanup) return installedCleanup;

  const onClick = (domEvent) => {
    handleHrmosDocumentClick(domEvent);
  };

  document.addEventListener('click', onClick, true);
  installedCleanup = () => {
    document.removeEventListener('click', onClick, true);
    installedCleanup = null;
  };
  return installedCleanup;
}
